import { useRef, useState } from 'react';
import { ApiError, type components } from '@awj/api-client';
import { Button, useToast } from '@awj/ui';

import { Icon } from '~/components/Icon.tsx';
import { useI18n } from '~/i18n/index.tsx';
import { useConfirm } from '~/lib/confirm.tsx';
import { useProductImages } from '~/lib/catalog-queries.ts';
import { mediaUrl } from '~/lib/media.ts';

type Image = components['schemas']['AdminProductImageResponse'];

/**
 * The product's photographs.
 *
 * Only available once the product exists, because an image is uploaded *against* a product id —
 * there is nowhere to put a file for something that has not been saved yet. The editor hides
 * this section on a new product rather than offering an upload that cannot work.
 *
 * Ordering is by arrow rather than drag: the first image is the one the shop shows in listings,
 * so "make this first" is the operation that matters, and it is one a keyboard can perform.
 */
export function ImageManager({ productId, images }: { productId: number; images: Image[] }) {
  const { t, locale } = useI18n();
  const toast = useToast();
  const fileInput = useRef<HTMLInputElement>(null);
  const { upload, updateAlt, remove, reorder } = useProductImages(productId);
  const confirm = useConfirm();

  const [editing, setEditing] = useState<number | null>(null);
  const [altAr, setAltAr] = useState('');
  const [altEn, setAltEn] = useState('');

  async function onFiles(files: FileList | null) {
    if (!files?.length) {
      return;
    }

    for (const file of Array.from(files)) {
      try {
        await upload.mutateAsync(file);
      } catch (error) {
        // Named, because "upload failed" for a batch tells nobody which one to try again.
        toast.show({
          title: t('catalog.uploadFailed', { name: file.name }),
          message: error instanceof ApiError ? error.problem.detail : undefined,
          tone: 'danger',
        });
      }
    }

    // Cleared so choosing the same file twice still fires a change event.
    if (fileInput.current) {
      fileInput.current.value = '';
    }
  }

  /**
   * A refusal, said out loud. Reorder, delete and the alt-text save used to fail in silence — the
   * spinner stopped as though the change had been made, and the next reload put it back.
   */
  function failed(title: string) {
    return (error: unknown) =>
      toast.show({
        title,
        message: error instanceof ApiError ? error.problem.detail : undefined,
        tone: 'danger',
      });
  }

  async function saveAlt(imageId: number) {
    try {
      await updateAlt.mutateAsync({ imageId, altAr, altEn });
      setEditing(null);
    } catch (error) {
      failed(t('catalog.couldNotSave'))(error);
    }
  }

  function move(index: number, delta: number) {
    const next = [...images];
    const target = index + delta;

    if (target < 0 || target >= next.length) {
      return;
    }

    [next[index], next[target]] = [next[target]!, next[index]!];
    reorder.mutate(
      next.map((image) => image.id),
      { onError: failed(t('catalog.couldNotSave')) },
    );
  }

  return (
    <section className="card" aria-labelledby="images-title">
      <div className="card__head">
        <h2 className="section-label" id="images-title">
          {t('catalog.images')}
        </h2>

        <Button
          type="button"
          variant="secondary"
          size="sm"
          loading={upload.isPending}
          onClick={() => fileInput.current?.click()}
        >
          {t('catalog.uploadImages')}
        </Button>

        <input
          ref={fileInput}
          className="sr-only"
          type="file"
          accept="image/*"
          multiple
          onChange={(event) => void onFiles(event.target.files)}
        />
      </div>

      {images.length === 0 ? (
        <p className="field__hint">{t('catalog.noImages')}</p>
      ) : (
        <ul className="image-grid">
          {images.map((image, index) => (
            <li className="image-card" key={image.id}>
              <div className="image-card__media">
                <img src={mediaUrl(image.url)!} alt={locale === 'ar' ? image.altAr ?? '' : image.altEn ?? ''} loading="lazy" />
                {index === 0 ? <span className="image-card__badge">{t('catalog.primaryImage')}</span> : null}
              </div>

              <div className="image-card__actions">
                <button
                  type="button"
                  className="icon-button"
                  disabled={index === 0}
                  aria-label={t('catalog.moveEarlier')}
                  title={t('catalog.moveEarlier')}
                  onClick={() => move(index, -1)}
                >
                  {/* Drawn for left-to-right, so mirrored only in Arabic, where earlier is to the right. */}
                  <Icon name="chevronStart" className="rtl-flip" />
                </button>
                <button
                  type="button"
                  className="icon-button"
                  disabled={index === images.length - 1}
                  aria-label={t('catalog.moveLater')}
                  title={t('catalog.moveLater')}
                  onClick={() => move(index, 1)}
                >
                  <Icon name="chevronEnd" className="rtl-flip" />
                </button>
                <button
                  type="button"
                  className="icon-button"
                  aria-label={t('catalog.editAlt')}
                  title={t('catalog.editAlt')}
                  onClick={() => {
                    setEditing(image.id);
                    setAltAr(image.altAr ?? '');
                    setAltEn(image.altEn ?? '');
                  }}
                >
                  <Icon name="edit" />
                </button>
                <button
                  type="button"
                  className="icon-button icon-button--danger"
                  aria-label={t('common.delete')}
                  title={t('common.delete')}
                  onClick={async () => {
                    if (await confirm.ask({ title: t('confirm.deleteImage'), body: t('confirm.deleteImageBody') })) {
                      remove.mutate(image.id, { onError: failed(t('catalog.couldNotDelete')) });
                    }
                  }}
                >
                  <Icon name="trash" />
                </button>
              </div>

              {editing === image.id ? (
                <div className="image-card__alt">
                  {/*
                    Alt text in both languages, because the shop is read in both. An image with
                    none is decorative and announced as nothing; one carrying meaning without it
                    is a gap only a screen-reader user ever hears.
                  */}
                  <input
                    className="awj-input"
                    lang="ar"
                    dir="rtl"
                    value={altAr}
                    placeholder={t('catalog.altAr')}
                    aria-label={`${t('catalog.alt')} — ${t('lang.arabic')}`}
                    onChange={(event) => setAltAr(event.target.value)}
                  />
                  <input
                    className="awj-input"
                    lang="en"
                    dir="ltr"
                    value={altEn}
                    placeholder={t('catalog.altEn')}
                    aria-label={`${t('catalog.alt')} — ${t('lang.english')}`}
                    onChange={(event) => setAltEn(event.target.value)}
                  />
                  <div className="image-card__altActions">
                    <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(null)}>
                      {t('common.cancel')}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      loading={updateAlt.isPending}
                      onClick={() => saveAlt(image.id)}
                    >
                      {t('common.save')}
                    </Button>
                  </div>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {confirm.dialog}
    </section>
  );
}
